import { Dispatch, FC, useMemo } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { clearItemInNewProduct, hideSecondaryPanel, changeNewProductField } from 'store/productManagement/reducer';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import tagSvg from 'assets/icons/tag.svg';
import SectionButton, { SectionButtonTheme } from 'lib/SectionButton';
import { SectionSelectedType } from 'lib/SectionButton/SectionButton.types';
import { Store } from 'components/common/types/Store.types';
import { ProductFieldName, SelectionPermission } from 'store/productManagement/reducer.types';
import { CodeNameModel } from 'components/common/types';
import { TagInclude } from 'components/common/types/Deal.types';
import { sortByCategoryIncludeExclude } from 'components/pages/Planner/PlannerSections/FiltersContent/utils';
import { DirectSalesCodeNameModel } from 'components/common/types/DirectSalesCampaign.types';
import { getIsReadOnly } from 'store/productManagement/selectors';
import ProductTagsPanel from './ProductTagsPanel';
import { isValueRequired } from '../../utils/isValueRequired';
import {
  getNoRestrictionsDisplayValue,
  getValueRequiredPermissionDisplayValue,
} from '../../utils/getNoRestrictionsDisplayValue';
import { PERMISSION_LABELS } from '../../consts';

const getTheme = (selectedPermission?: SelectionPermission): SectionButtonTheme => {
  return selectedPermission ? SectionButtonTheme.SOLID : SectionButtonTheme.DASHED;
};

const getSelectedTagsDisplayValue = (tags?: TagInclude[]): DirectSalesCodeNameModel[] | undefined => {
  if (!tags?.length) {
    return undefined;
  }

  return tags.map((tag) => ({
    code: tag.code,
    name: tag.name,
    category: tag.category,
    include: tag.include ?? true,
  }));
};

const getSelectedProp = (
  selectedDisplay?: DirectSalesCodeNameModel[],
  selectedPermission?: SelectionPermission,
): SectionSelectedType | undefined => {
  if (selectedDisplay?.length) {
    return sortByCategoryIncludeExclude(selectedDisplay);
  }

  if (selectedPermission && isValueRequired(selectedPermission)) {
    return getValueRequiredPermissionDisplayValue(selectedPermission);
  }

  return getNoRestrictionsDisplayValue(selectedPermission);
};

const handleTagChange = ({
  tags,
  tagsToRemove,
  included,
  selectedPermission,
  handleConfirm,
  dispatch,
  fieldName,
}: {
  tags: TagInclude[];
  tagsToRemove: string[];
  included?: boolean;
  selectedPermission: SelectionPermission;
  handleConfirm: ({ value, selectedPermission }: { value: TagInclude[]; selectedPermission: CodeNameModel }) => void;
  dispatch: Dispatch<AnyAction>;
  fieldName: ProductFieldName;
}): void => {
  const tagsSet = new Set(tagsToRemove);
  const newSelectedTags = tags.filter(
    (value) => !tagsSet.has(value.name) || (included !== undefined && value.include !== included),
  );

  if (newSelectedTags.length) {
    handleConfirm({
      value: newSelectedTags,
      selectedPermission: { code: selectedPermission, name: PERMISSION_LABELS[selectedPermission] },
    });
  } else {
    dispatch(clearItemInNewProduct({ fieldName }));
  }
};

const ProductTags: FC = () => {
  const dispatch = useDispatch();
  const isReadOnly = useSelector(getIsReadOnly);
  const mediaType = useSelector((state: Store) => state.productManagement.newProduct.mediaType?.value);
  const channels = useSelector((state: Store) => state.productManagement.newProduct.channels?.value);
  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const tags = useSelector((state: Store) => state.productManagement.newProduct.tags);
  const selectedPermission = tags?.selectionPermission;

  const onChangePanelVisibility = useOnChangePanelVisibility();
  const fieldName = ProductFieldName.TAGS;
  const isTagsPanelOpen = secondaryPanelType === PlannerSecondaryPanelType.TAG;

  const selectedDisplay = useMemo(() => getSelectedTagsDisplayValue(tags?.value), [tags?.value]);

  const handleConfirm = ({
    value,
    selectedPermission: newSelectedPermission,
  }: {
    value: TagInclude[];
    selectedPermission: CodeNameModel;
  }): void => {
    dispatch(
      changeNewProductField({
        fieldName,
        value: value.length ? value : undefined,
        selectedPermission: newSelectedPermission,
      }),
    );
    dispatch(hideSecondaryPanel());
  };

  const onRemoveTagChip = (tagsToRemove?: string[], included?: boolean): void => {
    if (!selectedPermission) return;

    if (!tagsToRemove) {
      if (included !== undefined) {
        const newSelectedTags = tags?.value?.filter((value) => value.include !== included) || [];
        if (!newSelectedTags.length) {
          dispatch(clearItemInNewProduct({ fieldName }));
          return;
        }

        handleConfirm({
          value: newSelectedTags,
          selectedPermission: { code: selectedPermission, name: PERMISSION_LABELS[selectedPermission] },
        });
        return;
      }

      dispatch(clearItemInNewProduct({ fieldName }));
      return;
    }

    if (!tagsToRemove.length) return;

    handleTagChange({
      tags: tags?.value || [],
      tagsToRemove,
      included,
      selectedPermission,
      handleConfirm,
      dispatch,
      fieldName,
    });
  };

  return (
    <>
      <SectionButton
        dataTestId="product-button-tags"
        label="Tags"
        icon={tagSvg}
        isDisabled={!mediaType || !channels?.length}
        isEllipsisDisabled={isReadOnly}
        theme={getTheme(selectedPermission)}
        isActive={isTagsPanelOpen}
        selected={getSelectedProp(selectedDisplay, selectedPermission)}
        onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.TAG)}
        onClear={() => {
          dispatch(clearItemInNewProduct({ fieldName }));
          dispatch(hideSecondaryPanel());
        }}
        onRemoveChip={onRemoveTagChip}
      />
      {isTagsPanelOpen && <ProductTagsPanel onConfirm={handleConfirm} isReadOnly={isReadOnly} />}
    </>
  );
};

export default ProductTags;
